import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Trophy, Flame, Zap, Star, RefreshCw } from 'lucide-react';

const BADGE_MAP = {
  speed_demon: { icon: Zap, label: 'Speed Demon', color: 'text-amber-500 bg-amber-100' },
  fire_fighter: { icon: Flame, label: 'Fire Fighter', color: 'text-red-500 bg-red-100' },
  top_rated: { icon: Star, label: 'Top Rated', color: 'text-[#D4A843] bg-[#D4A843]/10' },
  lightning_fast: { icon: Zap, label: 'Lightning Fast', color: 'text-emerald-500 bg-emerald-100' },
};

export default function LeaderboardView() {
  const [leaders, setLeaders] = useState([]);
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/api/enhance/leaderboard?period=${period}`);
        setLeaders(data.leaderboard || []);
      } catch { toast.error('Failed to load leaderboard'); }
      finally { setLoading(false); }
    })();
  }, [period]);

  if (loading) return <div className="flex items-center justify-center py-20"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leaderboard-view">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
            <Trophy className="w-6 h-6 text-amber-500" /> Technician Leaderboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">Rankings by resolution speed, critical fixes, and customer ratings</p>
        </div>
        <div className="flex gap-1">
          {['week', 'month'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${period === p ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`lb-period-${p}`}>
              This {p === 'week' ? 'Week' : 'Month'}
            </button>
          ))}
        </div>
      </div>

      {leaders.length === 0 && <p className="text-center text-gray-400 py-12 bg-white rounded-lg border border-gray-200">No resolved tickets in this period yet.</p>}

      <div className="space-y-3" data-testid="leaderboard-list">
        {leaders.map((tech, i) => (
          <div key={tech.user_id} className={`bg-white border rounded-lg p-5 flex items-center gap-4 ${i === 0 ? 'border-amber-300 bg-amber-50/30' : i === 1 ? 'border-gray-300' : i === 2 ? 'border-orange-200' : 'border-gray-200'}`} data-testid={`lb-tech-${tech.user_id}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 font-bold text-lg ${i === 0 ? 'bg-amber-500 text-white' : i === 1 ? 'bg-gray-400 text-white' : i === 2 ? 'bg-orange-400 text-white' : 'bg-gray-100 text-gray-500'}`}>
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900">{tech.name}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {tech.badges.map(b => {
                  const badge = BADGE_MAP[b];
                  if (!badge) return null;
                  const Icon = badge.icon;
                  return (
                    <span key={b} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold ${badge.color}`}>
                      <Icon className="w-3 h-3" /> {badge.label}
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4 text-center shrink-0">
              <div>
                <p className="text-lg font-bold text-gray-900">{tech.resolved}</p>
                <p className="text-[10px] text-gray-400">Resolved</p>
              </div>
              <div>
                <p className="text-lg font-bold text-red-600">{tech.critical_resolved}</p>
                <p className="text-[10px] text-gray-400">Critical</p>
              </div>
              <div>
                <p className="text-lg font-bold text-[#D4A843]">{tech.avg_resolution_min}m</p>
                <p className="text-[10px] text-gray-400">Avg Time</p>
              </div>
              <div>
                <p className="text-lg font-bold text-amber-500">{tech.score}</p>
                <p className="text-[10px] text-gray-400">Score</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
